import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Check, AlertCircle, X } from "lucide-react";
import { PricingPlanType } from "../../types/pricingPlans";
import { usePricingPlan } from "../../hooks/usePricingPlan";
import SubscriptionsAPI from "../../api/SubscriptionsAPI";
import PageWrapper from "../PageWrapper/PageWrapper";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import { toastService } from "../../services/ToastService";
import { getSubscriptionStatusKey, isSubscriptionPaymentFailed } from "../../utils/subscriptionStatusMapper";
import { generatePricingPlan, getThemeColorClasses, isLimitedFeature, sortIncludedFeaturesByAccess } from "../../utils/pricingPlanUtils";
import { ONBOARDING_BRAND } from "../../components/OnboardingFlow/onboardingBrand";

const PricingPlansPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentPlan, isLoadingSubscription, currentSubscription } = usePricingPlan();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const state = searchParams.get('state');
    if (state === 'success') {
      toastService.showToast(
        t('subscription.success_message', 'Subscription updated successfully! Changes may take a few minutes to take effect.'),
        'success'
      );
      setSearchParams({});
    } else if (state === 'cancel') {
      toastService.showToast(
        t('subscription.cancel_message', 'Subscription update was cancelled. No changes were made.'),
        'error'
      );
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, t]);

  // Experiment: hide Free from self-serve checkout; keep config/locales so this is reversible.
  const plans = ['store', 'brand', 'advanced-store'].map(planId => {
    const plan = generatePricingPlan(planId as PricingPlanType, planId === currentPlan.id);
    return {
      ...plan,
      name: t(plan.name, plan.name),
      description: t(plan.description, plan.description),
      features: sortIncludedFeaturesByAccess(
        plan.features.map(featureKey => ({
          key: featureKey,
          label: t(featureKey, featureKey),
        }))
      ),
      featuresNotIncluded: plan.featuresNotIncluded.map(featureKey => ({
        key: featureKey,
        label: t(featureKey, featureKey),
      })),
      note: plan.note ? t(plan.note, plan.note) as string : null,
      price: plan.price === 'Free' ? 'Free' : t(plan.price, plan.price) as string
    };
  });

  // Mark current plan
  plans.forEach(plan => {
    plan.isCurrentPlan = plan.id === currentPlan.id;
  });

  const isDowngrade = (planId: PricingPlanType, currentPlanId: PricingPlanType): boolean => {
    const planHierarchy = ['free', 'store', 'brand', 'advanced-store'];
    const currentIndex = planHierarchy.indexOf(currentPlanId);
    const planIndex = planHierarchy.indexOf(planId);
    return planIndex < currentIndex;
  };

  const handleSubscribe = async (plan: PricingPlanType) => {
    setIsLoading(true);
    try {
      const response = await SubscriptionsAPI.createCheckoutOrPortal(plan);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create checkout or get portal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await SubscriptionsAPI.createCheckoutOrPortal('current');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to get customer portal URL:', error);
    }
  };

  const canChangeTo = (planId: PricingPlanType) => {
    if (planId === 'early-adopter') return false;
    if (planId === currentPlan.id) return false;
    if (currentPlan.id === 'early-adopter' && planId === 'free') return false;
    
    return planId === 'free' || planId === 'store' || planId === 'brand' || planId === 'advanced-store';
  };

  const planActionButton = (plan: typeof plans[number]) => {
    if (plan.isCurrentPlan) {
      return (
        <PrimaryButton
          tone="brand"
          text={t('subscription.current_plan_badge', 'Current Plan') as string}
          isDisabled={true}
          fn={() => {}}
          actionName={t('subscription.current_plan_badge', 'Current Plan') as string}
        />
      );
    }
    if (canChangeTo(plan.id)) {
      const downgrade = isDowngrade(plan.id, currentPlan.id);
      return (
        <PrimaryButton
          tone="brand"
          text={
            downgrade
              ? (t('pricing.downgrade', 'Downgrade') as string)
              : (t('pricing.upgrade', 'Upgrade Now') as string)
          }
          fn={() => handleSubscribe(plan.id)}
          isDisabled={isLoading}
          actionName={`${downgrade ? 'Downgrade' : 'Upgrade'} to ${plan.name}`}
        />
      );
    }
    return (
      <PrimaryButton
        tone="brand"
        text={t('pricing.not_available', 'Not Available') as string}
        isDisabled={true}
        fn={() => {}}
        actionName={t('pricing.not_available', 'Not Available') as string}
      />
    );
  };

  if (isLoadingSubscription) {
    return (
      <PageWrapper>
        <div className={`${ONBOARDING_BRAND.scope} min-h-screen flex items-center justify-center`}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-as-pink-300 mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('subscription.loading', 'Loading subscription information...')}</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const isPaymentFailed = isSubscriptionPaymentFailed(currentSubscription?.status);

  return (
    <PageWrapper>
      <div className={`${ONBOARDING_BRAND.scope} min-h-screen`}>
        <div className="container mx-auto">

          {/* Payment failed warning */}
          {isPaymentFailed && (
            <div className="mb-6 max-w-7xl mx-auto rounded-lg border border-red-300 bg-red-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-red-800 text-sm">
                {t('subscription.payment_failed_warning', 'Your subscription payment could not be processed. Please check funds in your account, update your card, or follow the link below to take action and avoid service interruption.')}
              </p>
              <PrimaryButton
                tone="brand"
                text={t('subscription.payment_failed_cta', 'Resolve payment') as string}
                fn={handleManageSubscription}
                actionName="Resolve subscription payment"
              />
            </div>
          )}

          {/* Current Plan Banner */}
          {currentPlan && (
            <div className="bg-as-pink-100 border border-as-pink-200 rounded-lg p-4 mb-8 max-w-7xl mx-auto">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-as-pink-500">
                    {t('subscription.current_plan', 'Current Plan')}: {currentPlan.name}
                  </h3>
                  <p className="text-as-pink-500/90 text-sm">{currentPlan.description}</p>
                  {currentSubscription && (
                    <div className="mt-2 text-xs text-as-pink-500">
                      <span className="inline-block bg-white/70 dark:bg-white/10 px-2 py-1 rounded-full mr-2">
                        {t('subscription.status', 'Status:')} {t(getSubscriptionStatusKey(currentSubscription.status), 'Unknown')}
                      </span>
                      {currentSubscription.currentPeriodEnd && !currentSubscription.cancelAtPeriodEnd && (
                        <span className="inline-block bg-white/70 dark:bg-white/10 px-2 py-1 rounded-full">
                          {t('subscription.renews', 'Renews:')} {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      )}
                      {currentSubscription.cancelAtPeriodEnd && currentSubscription.currentPeriodEnd && (
                        <span className="inline-block bg-orange-100 text-orange-800 px-2 py-1 rounded-full ml-2">
                          {t('subscription.cancels_at_period_end', 'Cancels at period end')}: {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {/* Only show Manage Subscription button for paid plans */}
                {(currentPlan.id === 'store' || currentPlan.id === 'brand' || currentPlan.id === 'advanced-store') && (
                  <SecondaryButton
                    text={t('subscription.manage', 'Manage Subscription') || 'Manage Subscription'}
                    fn={handleManageSubscription}
                  />
                )}
              </div>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {plans.map((plan) => {
              const themeClasses = getThemeColorClasses(plan.themeColor);
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border-2 p-6 flex flex-col ${
                    plan.isCurrentPlan ? ONBOARDING_BRAND.recommendedCard : ONBOARDING_BRAND.idleSurface
                  }`}
                >
                  {plan.isCurrentPlan && (
                    <span className={ONBOARDING_BRAND.recommendedBadge}>
                      {t('pricing.currentPlan')}
                    </span>
                  )}
                  <h3 className={`text-xl font-bold ${themeClasses.text} mb-2 text-center`}>{plan.name}</h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">{plan.description}</p>
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-foreground">{plan.price}</div>
                    {plan.id !== 'free' && (
                      <p className="text-sm text-muted-foreground">{t('pricing.per_month', 'per month')}</p>
                    )}
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((feature) => (
                      <li key={feature.key} className="flex items-start gap-2 text-sm text-foreground">
                        {isLimitedFeature(feature.key) ? (
                          <AlertCircle className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" />
                        ) : (
                          <Check className="w-4 h-4 mt-0.5 text-green-500 shrink-0" />
                        )}
                        <span>{feature.label}</span>
                      </li>
                    ))}
                    {plan.featuresNotIncluded.map((feature) => (
                      <li key={feature.key} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <X className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                        <span>{feature.label}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-center">
                    {planActionButton(plan)}
                  </div>
                  {plan.id !== 'free' && (
                    <p className="text-xs text-muted-foreground text-center mt-3">
                      {t('onboarding.plan.cancel_anytime')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default PricingPlansPage;
